import React from 'react';
import { Palette, Circle, Triangle, Square, Zap } from 'lucide-react';

interface ColorHarmonySelectorProps {
  selectedHarmony: string;
  onHarmonyChange: (harmony: string) => void;
  useAPI: boolean;
}

const COLOR_HARMONIES = [
  {
    id: 'analogous',
    name: 'Analogous',
    description: 'Adjacent colors on the color wheel',
    icon: Circle,
    example: ['#FF6B6B', '#FF8E53', '#FF6B9D', '#C44569', '#F8B500']
  },
  {
    id: 'complementary',
    name: 'Complementary',
    description: 'Opposite colors on the color wheel',
    icon: Circle,
    example: ['#FF6B6B', '#FF8E53', '#6BCF7F', '#4ECDC4', '#45B7D1']
  },
  {
    id: 'triadic',
    name: 'Triadic',
    description: 'Three evenly spaced colors',
    icon: Triangle,
    example: ['#FF6B6B', '#6BCF7F', '#6B8CFF', '#FFD93D', '#FF6B9D']
  },
  {
    id: 'tetradic',
    name: 'Tetradic',
    description: 'Four evenly spaced colors',
    icon: Square,
    example: ['#FF6B6B', '#6BCF7F', '#6B8CFF', '#FFD93D', '#FF6B9D']
  },
  {
    id: 'split_complementary',
    name: 'Split Complementary',
    description: 'Base color + two adjacent to complement',
    icon: Zap,
    example: ['#FF6B6B', '#FF8E53', '#6BCF7F', '#4ECDC4', '#96CEB4']
  },
  {
    id: 'monochromatic',
    name: 'Monochromatic',
    description: 'Variations of a single hue',
    icon: Circle,
    example: ['#FFE5E5', '#FFB3B3', '#FF6B6B', '#E55555', '#CC4444']
  }
];

export const ColorHarmonySelector: React.FC<ColorHarmonySelectorProps> = ({
  selectedHarmony,
  onHarmonyChange,
  useAPI
}) => {
  if (useAPI) return null; // Only show for local generation

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-500 rounded-lg flex items-center justify-center">
          <Palette className="text-white" size={16} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Color Harmony</h3>
          <p className="text-sm text-gray-600">Choose a color theory algorithm for local generation</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {COLOR_HARMONIES.map((harmony) => {
          const IconComponent = harmony.icon;
          return (
            <div
              key={harmony.id}
              onClick={() => onHarmonyChange(harmony.id)}
              className={`p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                selectedHarmony === harmony.id
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <div className="flex items-center space-x-3 mb-3">
                <IconComponent 
                  size={20} 
                  className={selectedHarmony === harmony.id ? 'text-blue-600' : 'text-gray-500'} 
                />
                <h4 className={`font-semibold ${
                  selectedHarmony === harmony.id ? 'text-blue-900' : 'text-gray-800'
                }`}>
                  {harmony.name}
                </h4>
              </div>
              
              <p className={`text-sm mb-3 ${
                selectedHarmony === harmony.id ? 'text-blue-700' : 'text-gray-600'
              }`}>
                {harmony.description}
              </p>
              
              {/* Color preview */}
              <div className="flex space-x-1">
                {harmony.example.map((color, index) => (
                  <div
                    key={index}
                    className="w-6 h-6 rounded-full border border-white shadow-sm"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};